import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';

import { useTranslation } from 'react-i18next';

const VISIBLE_VALUES = {
    PRODUCTION: {
        PRODUCTION: [
            'GOODS_PRODUCED',
                'QUALITY_GOODS_PRODUCED',
                'FILLING_LINES',
                'PACKING_ROBOTS',
                'STORAGE_SPACE_REQUIREMENT',
                'PRODUCTION_FACILITY_COUNT',
            ],
    },
    SALES_AND_MARKETING: {
        SALES_AND_MARKETING: [
            'QUANTITY_OFFERED_TO_MARKETS',
            'QUANTITY_SOLD_DOMESTIC',
            'REVENUE_DOMESTIC',
            'QUANTITY_SOLD_EXPORT',
            'REVENUE_EXPORT',
        ],
    },
    INCOME_STATEMENT: {
        INCOME: [
            'REVENUE',
            'CHANGE_IN_FINISHED_GOODS_INVENTORY',
            'INCOME_STATEMENT_TOTAL_INCOME',
        ],
        EXPENSES: {
            PRODUCTION_COSTS: [
                'INCOME_STATEMENT_CONCENTRATE_COST',
                'INCOME_STATEMENT_BOTTLES_COST',
                'INCOME_STATEMENT_BOXES_COST',
            ],
            INDIRECT_PRODUCTION_COSTS: [
                'INCOME_STATEMENT_PRODUCTION_WORKERS_COST',
                'INCOME_STATEMENT_PROCUREMENT_SPECIALISTS_COST',
                'INCOME_STATEMENT_FILLING_LINES_DEPRECIATION',
                'INCOME_STATEMENT_PRODUCTION_LINE_FIXED_AND_VARIABLE_COSTS',
            ],
            TOTAL: [
                'INCOME_STATEMENT_TOTAL_PRODUCTION_COSTS',
            ],
            ADMINISTRATIVE_EXPENSES: [
                'INCOME_STATEMENT_MANAGEMENT_AND_MARKETING_COST',
                'INCOME_STATEMENT_ADVERTISEMENT_COST',
                'INCOME_STATEMENT_INNOVATION_COST',
                'INCOME_STATEMENT_TRAINING_COST',
                'INCOME_STATEMENT_SALESMEN_LABOR_COST',
            ],
            TOTAL_ADMINISTRATIVE_EXPENSES: [
                'INCOME_STATEMENT_TOTAL_ADMINISTRATIVE_EXPENSES',
            ],
            INTEREST_COST: [
                'INCOME_STATEMENT_INTEREST_COST',
            ],
            TOTAL_EXPENSES: [
                'INCOME_STATEMENT_TOTAL_EXPENSES',
            ],
            PROFIT: [
                'INCOME_STATEMENT_PROFIT',
            ],
        },
    },
    MATERIEL: {
        CONCENTRATE: [
            'CONCENTRATE_IN_STORAGE_START',
            'CONCENTRATE_IN_STORAGE_INCREASE',
            'CONCENTRATE_IN_STORAGE_DECREASE',
            'CONCENTRATE_IN_STORAGE_END',
        ],
        BOTTLES: [
            'BOTTLES_IN_STORAGE_START',
            'BOTTLES_IN_STORAGE_INCREASE',
            'BOTTLES_IN_STORAGE_DECREASE',
            'BOTTLES_IN_STORAGE_END',
        ],
        BOXES: [
            'BOXES_IN_STORAGE_START',
            'BOXES_IN_STORAGE_INCREASE',
            'BOXES_IN_STORAGE_DECREASE',
            'BOXES_IN_STORAGE_END',
        ],
    },
    PERSONNEL: {
        PRODUCTION_WORKERS: [
            'PRODUCTION_WORKERS',
        ],
        SALESMEN: [
            'SALESMEN',
        ],
        PROCUREMENT_SPECIALISTS: [
            'PROCUREMENT_SPECIALISTS',
        ],
    },
    BANK: {
        LONG_TERM_DEBT: [
            'LONG_TERM_DEBT',
            'LONG_TERM_DEBT_COST',
        ],
        SHORT_TERM_CREDIT: [
            'SHORT_TERM_CREDIT',
            'SHORT_TERM_CREDIT_COST',
            'SHORT_TERM_CREDIT_REPAYMENT',
        ],
        DEPOSITS: [
            'DEPOSITED_AMOUNT_AT_END_OF_PERIOD',
            'INTEREST_ON_DEPOSIT',
        ]
    },
};

const isNestedSubcategory = (subcategory) => !Array.isArray(subcategory);

const TITLELESS_SUBCATEGORIES = new Set([
    'TOTAL',
    'TOTAL_ADMINISTRATIVE_EXPENSES',
    'INTEREST_COST',
    'TOTAL_EXPENSES',
    'PROFIT',
]);

const BOLD_VALUE_NAMES = new Set([
    'INCOME_STATEMENT_TOTAL_INCOME',
    'INCOME_STATEMENT_TOTAL_PRODUCTION_COSTS',
    'INCOME_STATEMENT_TOTAL_ADMINISTRATIVE_EXPENSES',
    'INCOME_STATEMENT_INTEREST_COST',
    'INCOME_STATEMENT_TOTAL_EXPENSES',
    'INCOME_STATEMENT_PROFIT',
]);

const getVisibleValueNames = (subcategories) => {
    return Object.values(subcategories).flatMap(subcategory => {
        if (Array.isArray(subcategory)) {
            return subcategory;
        }

        return Object.values(subcategory).flat();
    });
};

const createValuePlaceholders = (valueNames) => {
    return valueNames.reduce((acc, name) => {
        acc[name] = {};
        return acc;
    }, {});
};

export const hasVisibleResults = (results, team, category) => {
    const subcategories = VISIBLE_VALUES[category];
    if (!results || !subcategories) return false;

    const teamName = team ?? Object.keys(results)[0];
    if (!teamName) return false;

    const teamResults = results[teamName];
    if (!teamResults) return false;

    const visibleValueNames = new Set(getVisibleValueNames(subcategories));
    return Object.values(teamResults).some(roundValues =>
        roundValues && Object.values(roundValues).some(value =>
            value?.category === category && visibleValueNames.has(value.name)
        )
    );
};

const byCategoryOrdered = (results, category) => {
    const subcategories = VISIBLE_VALUES[category];

    const getSubcategory = (name) => {
        for (const [subcategory, subcategoryValueNames] of Object.entries(subcategories)) {
            if (Array.isArray(subcategoryValueNames) && subcategoryValueNames.includes(name)) {
                return [subcategory];
            }

            if (isNestedSubcategory(subcategoryValueNames)) {
                for (const [nestedSubcategory, nestedValueNames] of Object.entries(subcategoryValueNames)) {
                    if (nestedValueNames.includes(name)) {
                        return [subcategory, nestedSubcategory];
                    }
                }
            }
        }
    };

    if (subcategories) {
        const categoryValues = Object.keys(subcategories).reduce((ac, subcategory) => {
            const subcategoryValueNames = subcategories[subcategory];
            if (Array.isArray(subcategoryValueNames)) {
                ac[subcategory] = createValuePlaceholders(subcategoryValueNames);
            } else {
                ac[subcategory] = {
                    groups: Object.entries(subcategoryValueNames).reduce((acc, [nestedSubcategory, nestedValueNames]) => {
                        acc[nestedSubcategory] = createValuePlaceholders(nestedValueNames);
                        return acc;
                    }, {}),
                };
            }
            return ac;
        }, {});

        Object.entries(results).forEach(([round, roundValues]) => {
            Object.entries(roundValues).forEach(([name, value]) => {
                const subcategoryPath = getSubcategory(name);
                if (value.category === category && subcategoryPath) {
                    const [subcategory, nestedSubcategory] = subcategoryPath;
                    const valueTarget = nestedSubcategory
                        ? categoryValues[subcategory].groups[nestedSubcategory]
                        : categoryValues[subcategory];

                    if (valueTarget[name] && valueTarget[name].values) {
                        valueTarget[name].values.push(value.value);
                    } else {
                        let valueWithValues = { ...value };
                        delete valueWithValues.value;
                        valueWithValues.values = [value.value];
                        valueTarget[name] = valueWithValues;
                    }
                }
            });
        });

        return categoryValues;
    } else {
        return {};
    }
};

const ResultRow = ({value}) => {
    const { t } = useTranslation();
    const name = t(`results.${value.category}.${value.name}.label`);
    const help = t(`results.${value.category}.${value.name}.description`);
    const unit = value.unit ? ('(' + t(`unit.${value.unit}`) + ')') : '';
    const isProfit = value.name === 'INCOME_STATEMENT_PROFIT';
    const isBold = BOLD_VALUE_NAMES.has(value.name);
    const rowLabelClassName = `align-middle${isBold ? ' fw-bold' : ''}`;
    return <tr key={value.name}>
        <td key="name" className={rowLabelClassName}>
            <Form.Label className="mb-0" title={help}>{name} {unit}</Form.Label>
        </td>
        {
            value.values.map((value, i) => {
                const numericValue = Number(value);
                const boldClassName = isBold ? ' fw-bold' : '';
                const profitClassName = isProfit
                    ? ` ${numericValue < 0 ? 'text-danger' : 'text-success'}`
                    : '';

                return <td key={i} className={`align-middle text-center${boldClassName}${profitClassName}`}>
                    {value}
                </td>;
            })
        }
    </tr>
};

const hasRenderedValues = (values) => {
    return Object.values(values).some(value => value.name && value.values);
};

const ValuesRows = ({ values }) => {
    const visibleValues = Object.entries(values).filter(([, value]) => value.name && value.values);
    return visibleValues.map(([, value]) => <ResultRow key={value.name} value={value} />);
};

const SubcategoryResults = ({ round, category, subcategory, subcategoryValues }) => {
    const { t } = useTranslation();
    let title = t(`results.${category}.subcategories.${subcategory}.title`);
    const nestedGroups = subcategoryValues.groups;
    return <>
        <tr key={subcategory}>
            <td key="name" className="align-middle fw-bold" colSpan={round}>
                <Form.Label className="mb-0">{title}</Form.Label>
            </td>
        </tr>
        {
            nestedGroups
                ? Object.entries(nestedGroups)
                    .filter(([, values]) => hasRenderedValues(values))
                    .map(([nestedSubcategory, values]) => <NestedSubcategoryResults
                        key={nestedSubcategory}
                        round={round}
                        category={category}
                        subcategory={nestedSubcategory}
                        subcategoryValues={values}
                    />)
                : <ValuesRows values={subcategoryValues} />
        }
    </>
};

const NestedSubcategoryResults = ({ round, category, subcategory, subcategoryValues }) => {
    const { t } = useTranslation();
    let title = t(`results.${category}.subcategories.${subcategory}.title`);
    if (TITLELESS_SUBCATEGORIES.has(subcategory)) {
        return <ValuesRows values={subcategoryValues} />;
    }

    return <>
        <tr key={subcategory}>
            <td key="name" className="align-middle fw-bold ps-4" colSpan={round}>
                <Form.Label className="mb-0">{title}</Form.Label>
            </td>
        </tr>
        <ValuesRows values={subcategoryValues} />
    </>
};

const Results = ({ results, team, round, rounds, category }) => {
    const { t } = useTranslation();
    if (!hasVisibleResults(results, team, category)) return null;

    const teamName = team ?? Object.keys(results)[0];
    const teamResults = results[teamName];
    const categoryResults = byCategoryOrdered(teamResults, category);
    return <div style={{ display: "inline-block" }}>
        <Table striped>
            <thead>
                <tr key={category}>
                    <th key="name" className="decisions-table-name-header">{t(`game.results`)}</th>
                    {Array.from({ length: rounds }, (_, i) => {
                        return <th key={i} className="decisions-table-header">{`${t('games.round')} ${i + 1}`}</th>;
                    })}
                </tr>
            </thead>
            <tbody>
                {
                    Object.entries(categoryResults).map(([subcategory, values]) => <SubcategoryResults key={subcategory} round={round} category={category} subcategory={subcategory} subcategoryValues={values} />)
                }
            </tbody>
        </Table>
    </div>
};

export default Results;
